import React from 'react';
import { CheckCircle, Eye, EyeOff } from 'lucide-react';
import { passwordRequirements } from './types';

interface PasswordFormProps {
  password: string;
  confirmPassword: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  passwordError: string;
  confirmError: string;
  setPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  setShowPassword: (value: boolean) => void;
  setShowConfirmPassword: (value: boolean) => void;
  validatePassword: (value: string) => boolean;
  validateConfirmPassword: (value: string) => boolean;
  onSubmit: (e: React.FormEvent) => void;
}

export const PasswordForm: React.FC<PasswordFormProps> = ({
  password,
  confirmPassword,
  showPassword,
  showConfirmPassword,
  passwordError,
  confirmError,
  setPassword,
  setConfirmPassword,
  setShowPassword,
  setShowConfirmPassword,
  validatePassword,
  validateConfirmPassword,
  onSubmit,
}) => {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <label
          htmlFor="password"
          className="block text-sm font-medium"
          style={{ color: 'var(--color-text-primary)' }}
        >
          New Password <span style={{ color: 'var(--color-error, #ef4444)' }}>*</span>
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) validatePassword(e.target.value);
              if (confirmPassword && confirmError) validateConfirmPassword(confirmPassword);
            }}
            onBlur={() => validatePassword(password)}
            placeholder="Enter your new password"
            className="w-full px-4 py-3 pr-12 rounded-lg text-sm transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              border: passwordError
                ? '1px solid var(--color-error, #ef4444)'
                : '1px solid var(--color-border-default)',
              color: 'var(--color-text-primary)',
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        {passwordError && (
          <p className="text-xs" style={{ color: 'var(--color-error, #ef4444)' }}>
            {passwordError}
          </p>
        )}
      </div>

      <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
        <p className="text-xs font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
          Password must have:
        </p>
        <div className="grid grid-cols-2 gap-1">
          {passwordRequirements.map((req) => (
            <div key={req.label} className="flex items-center gap-1.5">
              <div
                className={`w-3 h-3 rounded-full flex items-center justify-center ${
                  req.test(password) ? 'bg-green-500' : ''
                }`}
                style={{
                  backgroundColor: req.test(password) ? undefined : 'var(--color-border-default)',
                }}
              >
                {req.test(password) && <CheckCircle className="w-2.5 h-2.5 text-white" />}
              </div>
              <span
                className="text-xs"
                style={{
                  color: req.test(password)
                    ? 'var(--color-accent-green)'
                    : 'var(--color-text-tertiary)',
                }}
              >
                {req.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label
          htmlFor="confirmPassword"
          className="block text-sm font-medium"
          style={{ color: 'var(--color-text-primary)' }}
        >
          Confirm Password <span style={{ color: 'var(--color-error, #ef4444)' }}>*</span>
        </label>
        <div className="relative">
          <input
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (confirmError) validateConfirmPassword(e.target.value);
            }}
            onBlur={() => validateConfirmPassword(confirmPassword)}
            placeholder="Confirm your new password"
            className="w-full px-4 py-3 pr-12 rounded-lg text-sm transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              border: confirmError
                ? '1px solid var(--color-error, #ef4444)'
                : '1px solid var(--color-border-default)',
              color: 'var(--color-text-primary)',
            }}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        {confirmError && (
          <p className="text-xs" style={{ color: 'var(--color-error, #ef4444)' }}>
            {confirmError}
          </p>
        )}
      </div>

      <button
        type="submit"
        className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base font-medium"
      >
        Reset Password
      </button>
    </form>
  );
};
